import React, { FunctionComponent, useState, useEffect, useRef } from 'react'
import { injectIntl, InjectedIntlProps } from 'react-intl'
import { debounce } from 'src/lib/debounce'
import dompurify from 'dompurify'
import { EventSection } from 'src/components/calendar/eventSection'
import { LoadingContent } from 'src/components/loading-content/LoadingContent'
import { LoadingPlaceholder } from 'src/components/loading-content/LoadingPlaceholder'
import { videoUrlsRegex } from 'src/lib/video-urls';
import { DESCRIPTION_ID } from './constants'

const style = require('./Description.css')
import locale from './locale'

const MAX_DESCRIPTION_HEIGHT = 250

interface Props {
  eventData: any,
  isMobile: Boolean,
  loading: Boolean,
}

const Placeholder = () => (
  <LoadingPlaceholder modifierClass={style.placeholder}>
    <LoadingContent width="170px" height="25px" borderRadius="rounder" />

    <LoadingContent height="14px" />
    <LoadingContent height="14px" />
    <LoadingContent height="14px" />
    <LoadingContent width="80%" height="14px" />

    <LoadingContent height="14px" />
    <LoadingContent height="14px" />
    <LoadingContent width="80%" height="14px" />
  </LoadingPlaceholder>
)

const DescriptionComponent: FunctionComponent<Props & InjectedIntlProps> = ({
  eventData,
  isMobile,
  loading,
  intl: { formatMessage },
}) => {
  const [isExpanded, setExpanded] = useState(false);
  const [isExpandBtnDisplayed, setExpandBtnDisplayed] = useState(false);
  const descriptionRef = useRef(null);

  useEffect(() => {
    if ((isMobile) && (getDescriptionHeight() >= MAX_DESCRIPTION_HEIGHT)) {
      setExpandBtnDisplayed(true)
    }

    // Store the window width so we can check in resize listener function if window width has been changed
    const windowWidth = window.innerWidth

    window.addEventListener('resize', debouncedHandler.bind(null, windowWidth))

    return () => window.removeEventListener('resize', debouncedHandler)
  }, [])

  useEffect(() => {
    if ((isMobile) && (getDescriptionHeight() >= MAX_DESCRIPTION_HEIGHT)) {
      setExpandBtnDisplayed(true)
    }
    if ((!isMobile) && isExpandBtnDisplayed) {
      setExpandBtnDisplayed(false)
    }
  })

  const toggleExpandBtnVisibility = () => {
    if (getDescriptionHeight() >= MAX_DESCRIPTION_HEIGHT) {
      setExpanded(false)
      setExpandBtnDisplayed(true)
    } else {
      setExpandBtnDisplayed(false)
    }
  }

  const debouncedHandler = (windowWidth) => {
    // Check window width has actually changed and it's not just iOS/Android triggering a resize event on scroll
    if (window.innerWidth != windowWidth) {

      // Update the window width for next time
      windowWidth = window.innerWidth

      // Toggle the expand button
      debounce(toggleExpandBtnVisibility, 50)
    }
  }

  const getDescriptionHeight = () => descriptionRef.current && descriptionRef.current.clientHeight

  const buttonText = () => {
    const expandText = formatMessage(locale.showMore)
    const collapseText = formatMessage(locale.showLess)

    return isExpanded ? collapseText : expandText
  }

  const getDescriptionClass = () => (
    isExpandBtnDisplayed && !isExpanded
      ? `${style.description} ${style.descriptionCollapsed}`
      : style.description
  )

  const toggleDescriptionOpen = () => setExpanded(!isExpanded)

  // https://github.com/cure53/DOMPurify/tree/main/demos#hook-to-open-all-links-in-a-new-window-link
  dompurify.addHook('afterSanitizeAttributes', node => {
    if ('target' in node) node.setAttribute('target', '_blank');
  });

  if (loading) return <Placeholder />

  return (
    <EventSection sectionId={DESCRIPTION_ID} heading={formatMessage(locale.description)}>
      <div
        className={getDescriptionClass()}
        ref={descriptionRef}
        dangerouslySetInnerHTML={{ __html: dompurify.sanitize(
          eventData.description,
          {
            ALLOWED_TAGS: [
              'p',
              'a',
              'hr',
              'em',
              'strong',
              'u',
              'blockquote',
              'pre',
              'span',
              'del',
              'h1',
              'h2',
              'h3',
              'h4',
              'h5',
              'br',
              'figure',
              'img',
              'ul',
              'ol',
              'li',
              'table',
              'tbody',
              'tr',
              'td',
            ],
            ADD_TAGS: ['iframe'],
            ADD_URI_SAFE_ATTR: [
              'frameborder',
              'allow',
              'allowfullscreen',
              'allowtransparency',
              'width',
              'height',
              'scrolling',
              'style',
              'src',
              'href',
              'rel',
            ],
            ADD_ATTR: [
              'frameborder',
              'allow',
              'allowfullscreen',
              'allowtransparency',
              'src',
              'href',
              'style',
              'scrolling'
            ],
            ALLOWED_URI_REGEXP: videoUrlsRegex,
            ALLOW_DATA_ATTR: false,
          }
        ) }} // eslint-disable-line react/no-danger
      />
      {
        isExpandBtnDisplayed &&
          <button className={style.button} onClick={toggleDescriptionOpen} >
            {buttonText()}
          </button>
      }
    </EventSection>
  )
}

export const Description = injectIntl(DescriptionComponent)
